import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { COPY } from "../../copy";
import type { P8MonthlyRoasPoint } from "../../p8OverviewData";
import { formatOverviewRoas } from "../../p8OverviewData";
import { forecastTooltip } from "../ForecastExplainer";

import {
  ChartHeadline,
  ChartLegend,
  ChartMonthAxisTick,
  ChartNavButtons,
  CHART_LEGEND_GAP_CLASS,
  CHART_MARGIN_WITH_AXIS_LABELS,
  CHART_Y_AXIS_WIDTH,
  chartXAxisLabel,
  type ChartLegendItem,
} from "./chartShared";

const IMMEDIATE_COLOR = "#2d2a70";
const FIRST_YEAR_COLOR = "#AFC1FF";

const Y_AXIS_TICK_COUNT = 5;
const NICE_ROAS_STEPS = [
  0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10, 12.5, 15, 20, 25, 50,
] as const;

type MonthlyRoasChartProps = {
  data: P8MonthlyRoasPoint[];
  /**
   * False on reorder ads, whose sales all land in the month they happen: there
   * is no maturing cohort, so there is no first-year return to forecast. The
   * second line and its legend entry drop out rather than tracing the immediate
   * one exactly.
   */
  showFirstYear?: boolean;
};

const IMMEDIATE_LEGEND_ITEM: ChartLegendItem = {
  key: "immediateRoas",
  label: "Immediate ROAS",
  color: IMMEDIATE_COLOR,
  tooltip: COPY.overview.charts.roas.immediate,
};

// The only forecasted series in this chart, so it's the only legend item whose
// tooltip carries the explainer link.
const FIRST_YEAR_LEGEND_ITEM: ChartLegendItem = {
  key: "firstYearRoas",
  label: "First-year ROAS (forecasted)",
  color: FIRST_YEAR_COLOR,
  tooltip: forecastTooltip(COPY.overview.charts.roas.firstYear),
  interactiveTooltip: true,
};

type SquareDotProps = { cx?: number; cy?: number; fill?: string };

function SquarePlotDot({ cx = 0, cy = 0, fill = "#333" }: SquareDotProps) {
  return (
    <rect
      x={cx - 3}
      y={cy - 3}
      width={6}
      height={6}
      rx={1}
      ry={1}
      fill={fill}
    />
  );
}

function computeYAxisLayout(
  data: P8MonthlyRoasPoint[],
  showFirstYear: boolean
) {
  // First-year is the taller line when it's plotted; without it the axis should
  // close in on the immediate one rather than leaving its headroom empty.
  const dataMax = Math.max(
    0,
    ...data.map((point) =>
      showFirstYear ? point.firstYearRoas : point.immediateRoas
    )
  );
  const minStep = Math.max(dataMax, 1) / (Y_AXIS_TICK_COUNT - 1);
  const step = NICE_ROAS_STEPS.find((candidate) => candidate >= minStep) ?? 100;
  const top = step * (Y_AXIS_TICK_COUNT - 1);
  const ticks = Array.from(
    { length: Y_AXIS_TICK_COUNT },
    (_, index) => index * step
  );
  return { domain: [0, top] as [number, number], ticks };
}

type TooltipEntry = { dataKey?: string | number; value?: number };

type ChartTooltipProps = {
  active?: boolean;
  label?: string;
  payload?: TooltipEntry[];
  items: ChartLegendItem[];
};

function ChartTooltip({ active, label, payload, items }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  return (
    <div className="bg-fs-surface-primary rounded-md border border-[#dfe0e1] px-3 py-2 text-[14px] leading-[20px] text-[#333] shadow-[0_2px_8px_rgba(0,0,0,0.15)]">
      <div className="mb-1 font-medium">{label}</div>
      {items.map((item) => {
        const entry = payload.find((point) => point.dataKey === item.key);
        return (
          <div key={item.key} className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 rounded-[2px]"
              style={{ backgroundColor: item.color }}
            />
            <span>{item.label}</span>
            <span className="ml-auto font-medium">
              {formatOverviewRoas(entry?.value ?? 0)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function MonthlyRoasChart({
  data,
  showFirstYear = true,
}: MonthlyRoasChartProps) {
  const { domain, ticks } = computeYAxisLayout(data, showFirstYear);
  const legendItems = showFirstYear
    ? [IMMEDIATE_LEGEND_ITEM, FIRST_YEAR_LEGEND_ITEM]
    : [IMMEDIATE_LEGEND_ITEM];

  return (
    <div className="bg-fs-surface-primary flex h-full min-h-0 w-full flex-col rounded-lg border border-[#dfe0e1] p-6">
      <ChartHeadline
        title={COPY.overview.charts.roas.title}
        description={
          showFirstYear
            ? COPY.overview.charts.roas.description
            : COPY.overview.charts.roas.descriptionReorder
        }
        actions={<ChartNavButtons />}
      />

      <div
        className={`mt-4 flex min-h-0 flex-1 flex-col ${CHART_LEGEND_GAP_CLASS}`}
      >
        <div className="min-h-0 flex-1">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={CHART_MARGIN_WITH_AXIS_LABELS}>
              <CartesianGrid
                stroke="#DFE0E1"
                strokeDasharray="3 0"
                vertical={false}
              />
              <XAxis
                dataKey="month"
                type="category"
                scale="point"
                padding={{ left: 16, right: 16 }}
                tickLine={false}
                axisLine={{ stroke: "#DFE0E1" }}
                interval={0}
                height={44}
                tick={(props) => <ChartMonthAxisTick {...props} />}
                label={chartXAxisLabel("Month")}
              />
              <YAxis
                domain={domain}
                ticks={ticks}
                interval={0}
                tick={{ fontSize: 11, fill: "#757575" }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value: number) => formatOverviewRoas(value)}
                width={CHART_Y_AXIS_WIDTH}
              />
              <Tooltip
                cursor={{ stroke: "#dfe0e1", strokeWidth: 1 }}
                content={(props) => (
                  <ChartTooltip
                    {...(props as unknown as ChartTooltipProps)}
                    items={legendItems}
                  />
                )}
              />
              <Line
                type="linear"
                dataKey="immediateRoas"
                stroke={IMMEDIATE_COLOR}
                strokeWidth={1.5}
                dot={(props: SquareDotProps) => (
                  <SquarePlotDot {...props} fill={IMMEDIATE_COLOR} />
                )}
                activeDot={(props: SquareDotProps) => (
                  <SquarePlotDot {...props} fill={IMMEDIATE_COLOR} />
                )}
              />
              {showFirstYear ? (
                <Line
                  type="linear"
                  dataKey="firstYearRoas"
                  stroke={FIRST_YEAR_COLOR}
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={(props: SquareDotProps) => (
                    <SquarePlotDot {...props} fill={FIRST_YEAR_COLOR} />
                  )}
                  activeDot={(props: SquareDotProps) => (
                    <SquarePlotDot {...props} fill={FIRST_YEAR_COLOR} />
                  )}
                />
              ) : null}
            </LineChart>
          </ResponsiveContainer>
        </div>

        <ChartLegend items={legendItems} />
      </div>
    </div>
  );
}
